<script setup>
import ContenedorSelectores from "@/components/ContenedorSelectores";
import FichasVariantes from "@/components/FichasVariantes";
import GraficaEvolucionVariantes from "@/components/GraficaEvolucionVariantes";
import MapaVariantes from "@/components/MapaVariantes";
import NomenclaturaVariantes from "@/components/NomenclaturaVariantes";
import GraficaStatusPaciente from "@/components/GraficaStatusPacientes";
import TablaDatosDescarga from "@/components/TablaDatosDescarga";
import LinkCoverImg from "@/components/base/LinkCoverImg.vue";
import { computed } from "vue";
import store from "@/store/index.js";

const distribucionClases = computed(() => {
  return `variantes-${store.getters.varianteSeleccionada === "VTODAS" ? "all" : "1"}-estados-${
    store.getters.estadoSeleccionado === "00" ? "all" : "1"
  }`;
});
</script>

<template>
  <div>
    <div class="container m-t-1-sm m-t-2-md m-b-1">
      <h1 class="titular-portada m-b-1-sm m-b-1-md m-x-auto">
        Vigilancia de variantes del virus SARS-CoV-2
      </h1>

      <div class="alerta nota-pausa m-x-auto">
        <b class="alerta-titulo"><span class="alerta-titulo-icono">i</span>Atento aviso</b>
        <p class="alerta-texto">
          El 16 de marzo de 2023, la Organización Mundial de la Salud (OMS) actualizó su sistema de seguimiento y
          definiciones de trabajo de las variantes del virus SARS-CoV-2 derivado principalmente de que más del 98%
          de secuencias genéticas públicamente disponibles a nivel mundial desde febrero del 2022 corresponden a virus
          ómicron<sup>1</sup>. Posteriormente, el 5 de mayo, el Director General de la OMS manifestó su acuerdo con el
          asesoramiento presentado por el Comité de Emergencias del Reglamento Sanitario Internacional (2005) sobre la
          pandemia de enfermedad por coronavirus (COVID-19), dónde se determina que "la Covid-19 es ahora un problema
          de salud establecido y persistente que ya no constituye una emergencia de salud pública de importancia
          internacional (SPII)"<sup>2</sup>. A raíz de esta situación, el 9 de mayo el titular de la Subsecretaría de
          Prevención y Promoción de la Salud (SPPS) informó que se firmó el decreto "que pone fin a la vigencia del
          decreto original del 23 de marzo de 2020, en el que se estableció la situación de emergencia sanitaria
          asociada a COVID-19."<sup>3</sup> Por estos motivos, la actualización de los datos de este subcapítulo se
          pondrá en pausa a partir del 31 de mayo de 2023 con los últimos datos disponibles al momento.
        </p>
      </div>
    </div>

    <ContenedorSelectores class="m-y-2-md a11y-simplificada-none" />

    <div class="container main m-y-2 distribucion-elementos-variantes" :class="distribucionClases">
      <FichasVariantes class="ctn-fichas-resumen" />

      <GraficaEvolucionVariantes class="ctn-vis-evolucion card padding-0 a11y-simplificada-none" />

      <MapaVariantes class="ctn-mapa a11y-simplificada-none" />

      <NomenclaturaVariantes class="ctn-nomenclatura card" />

      <GraficaStatusPaciente class="ctn-vis-histograma card padding-0 a11y-simplificada-none" />

      <TablaDatosDescarga class="ctn-tabla-descarga m-t-1" />

      <div class="ctn-notas seccion-notas m-t-1">
        <div class="texto-notas ancho-lectura">
          <p><strong>Notas</strong></p>

          <p>
            La información presentada en el sitio tiene como objetivo explorar la diversidad y
            distribución de variantes del virus SARS-CoV-2 en el país, obtenidas a través de
            secuenciación genómica.
            <b>
              Las variantes que se muestran no pertenecen a la totalidad de los casos de cada
              entidad.
            </b>
            No obstante, permiten monitorear la distribución de las variantes del virus a través del
            tiempo. Los datos que se exhiben pertenecen al estudio realizado por el Instituto de
            Diagnóstico y Referencia Epidemiológicos, Instituto Nacional de Medicina Genómica,
            Instituto Nacional de Enfermedades Respiratorias, Universidad Autónoma de Nuevo León,
            Laboratorio Estatal de Salud Pública de Nuevo León, Universidad de Guadalajara,
            Universidad Autónoma de Yucatán, Universidad de Guerrero, Universidad Autónoma de San
            Luis Potosí, El Consorcio Mexicano de Vigilancia Genómica (INER, UNAM, CINVESTAV, IMSS),
            SCRIPPS (EEUU) y CDC (NAPAPI, EEUU).
          </p>

          <ol>
            <li>
              Declaración sobre la actualización de las definiciones de trabajo y del sistema de
              seguimiento de las variantes de preocupación y las variantes de interés del SARS-CoV-2
              Organización Mundial de la Salud. (2023, Marzo 16).
              <a href="https://www.who.int/es/news/item/16-03-2023-statement-on-the-update-of-who-s-working-definitions-and-tracking-system-for-sars-cov-2-variants-of-concern-and-variants-of-interest" target="_blank" rel="noopener noreferrer">
                https://www.who.int/es/news/item/16-03-2023-statement-on-the-update-of-who-s-working-definitions-and-tracking-system-for-sars-cov-2-variants-of-concern-and-variants-of-interest
              </a>
            </li>
            <li>
              Declaración acerca de la decimoquinta reunión del Comité de Emergencias del Reglamento
              Sanitario Internacional (2005) sobre la pandemia de enfermedad por coronavirus
              (COVID-19) Organización Mundial de la Salud. (2023, Mayo 05).
              <a href="https://www.who.int/es/news/item/05-05-2023-statement-on-the-fifteenth-meeting-of-the-international-health-regulations-(2005)-emergency-committee-regarding-the-coronavirus-disease-(covid-19)-pandemic" target="_blank" rel="noopener noreferrer">
                https://www.who.int/es/news/item/05-05-2023-statement-on-the-fifteenth-meeting-of-the-international-health-regulations-(2005)-emergency-committee-regarding-the-coronavirus-disease-(covid-19)-pandemic
              </a>
            </li>
            <li>
              México pone fin a la emergencia sanitaria por COVID-19: Secretaría de Salud. Gobierno
              de México. (2023, Mayo 9).
              <a href="https://www.gob.mx/salud/prensa/mexico-pone-fin-a-la-emergencia-sanitaria-por-covid-19-secretaria-de-salud?idiom=es" target="_blank" rel="noopener noreferrer">
                https://www.gob.mx/salud/prensa/mexico-pone-fin-a-la-emergencia-sanitaria-por-covid-19-secretaria-de-salud?idiom=es
              </a>
            </li>
          </ol>
        </div>

        <div>
          <LinkCoverImg
            :cover="require('../assets/img/repositorio.png')"
            url="https://covid-19.conacyt.mx/jspui/"
            target="_blank"
            title="Repositorio Conahcyt Covid-19"
            :degradado_activo="false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.alerta.nota-pausa {
  max-width: 100%;
  border: 1px solid #333;
  border-left-width: 8px;
  border-radius: 5px;
  padding: 16px;
  font-size: 14px;

  .alerta-titulo{
    display: flex;
    align-items: center;
    gap: 10px;

    &-icono {
      width: 24px;
      height: 24px;
      line-height: 24px;
      background: #000;
      color: #fff;
      text-align: center;
      border-radius: 50%;
    }
  }

  p {
    margin-bottom: 0;
  }
}

.distribucion-elementos-variantes {
  @media (min-width: map-get($media-queries-limit, "mobile")) {
    display: grid;
    grid-gap: 10px;
    grid-template-columns: calc(70% - 10px) 30%;
    grid-template-rows: auto;

    &.variantes-all-estados-all {
      .ctn-fichas-resumen {
        grid-column: 2 / 3;
        grid-row: 1 / 3;
      }

      .ctn-nomenclatura,
      .ctn-vis-histograma,
      .ctn-tabla-descarga,
      .ctn-notas {
        grid-column: 1 / 3;
      }
    }

    &.variantes-1-estados-all {
      .ctn-vis-evolucion {
        grid-column: 1 / 3;
        grid-row: 1;
      }

      .ctn-mapa {
        grid-column: 1 / 2;
        grid-row: 2 / 4;
      }

      .ctn-fichas-resumen {
        grid-column: 2 / 3;
        grid-row: 2 / 3;
      }

      .ctn-nomenclatura {
        grid-column: 2 / 3;
        grid-row: 3 / 4;
      }

      .ctn-vis-histograma,
      .ctn-tabla-descarga,
      .ctn-notas {
        grid-column: 1 / 3;
      }
    }
    
    &.variantes-1-estados-1 {
      .ctn-vis-evolucion {
        display: none;
      }

      .ctn-mapa {
        grid-column: 1 / 2;
        grid-row: 1 / 3;
      }

      .ctn-vis-histograma,
      .ctn-tabla-descarga,
      .ctn-notas {
        grid-column: 1 / 3;
      }
    }

    &.variantes-all-estados-1 {
      .ctn-vis-evolucion {
        display: none;
      }

      .ctn-fichas-resumen {
        grid-column: 2 / 3;
        grid-row: 1 / 3;
      }

      .ctn-mapa, .ctn-nomenclatura {
        grid-column: 1 / 2;
      }

      .ctn-vis-histograma,
      .ctn-tabla-descarga,
      .ctn-notas {
        grid-column: 1 / 3;
      }
    }
  }
}

.a11y-simplificada {
  .distribucion-elementos-variantes {
    grid-template-columns: calc(60% - 10px) 40%;

    &.variantes-all-estados-all,
    &.variantes-1-estados-all,
    &.variantes-1-estados-1,
    &.variantes-all-estados-1 {
      .ctn-fichas-resumen {
        grid-column: 2 / 3;
        grid-row: 1 / 5;
      }

      .ctn-nomenclatura {
        grid-row: 1;
      }

      .ctn-nomenclatura,
      .ctn-tabla-descarga,
      .ctn-notas {
        grid-column: 1 / 2;
      }
    }

    .seccion-notas {
      flex-direction: column;
    }
  }
}

.card {
  border: 1px solid #333;
  border-radius: 10px;
  padding: 10px;
  margin: 10px 0;
  position: relative;

  @media (min-width: map-get($media-queries-limit, "mobile")) {
    margin: 0px;
  }

  &.padding-0 {
    padding: 0;
  }
}

.seccion-notas {
  * {
    font-size: 12px;
  }

  @media (min-width: map-get($media-queries-limit, "mobile")) {
    display: flex;
    flex-direction: row;
    gap: 12px;
    justify-content: center;
  }
}
</style>
